/**
 * Centralized localStorage service
 * Provides a consistent interface for all localStorage operations
 * with error handling and validation
 */

import ErrorHandlingService from '../notifications/errorHandlingService';
import { authStorage, tenantStorageKey } from '../../tenancy/authStorage.js';

class StorageService {
    static notifyAuthChanged() {
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('atrux:auth-changed'));
        }
    }

    // Storage keys constants
    static KEYS = {
        AUTH_TOKEN: 'authToken',
        LAST_ROUTE: 'lastRoute',
        USER_DATA: 'userData',
        PREFERENCES: 'userPreferences'
    };

    static TENANT_KEY_NAMES = {
        authToken: 'token',
        lastRoute: 'last-route',
        userData: 'user',
        userPreferences: 'preferences',
        tokenExpiry: 'token-expiry'
    };

    static resolveKey(key) {
        const scopedName = this.TENANT_KEY_NAMES[key];
        return scopedName ? tenantStorageKey(scopedName) : key;
    }

    /**
     * Get item from localStorage with error handling
     * @param {string} key - The key to retrieve
     * @returns {string|null} The value or null if not found/error
     */
    static getItem(key) {
        try {
            const value = localStorage.getItem(this.resolveKey(key));
            return value;
        } catch (error) {
            ErrorHandlingService.handleError(error, `STORAGE_GET_${key}`);
            return null;
        }
    }

    /**
     * Set item in localStorage with error handling
     * @param {string} key - The key to set
     * @param {string} value - The value to set
     * @returns {boolean} Success status
     */
    static setItem(key, value) {
        try {
            localStorage.setItem(this.resolveKey(key), value);
            return true;
        } catch (error) {
            ErrorHandlingService.handleError(error, `STORAGE_SET_${key}`);
            return false;
        }
    }

    /**
     * Remove item from localStorage with error handling
     * @param {string} key - The key to remove
     * @returns {boolean} Success status
     */
    static removeItem(key) {
        try {
            localStorage.removeItem(this.resolveKey(key));
            return true;
        } catch (error) {
            ErrorHandlingService.handleError(error, `STORAGE_REMOVE_${key}`);
            return false;
        }
    }

    /**
     * Clear all localStorage with error handling
     * @returns {boolean} Success status
     */
    static clear() {
        return this.clearAuthData();
    }

    /**
     * Get JSON object from localStorage
     * @param {string} key - The key to retrieve
     * @returns {object|null} Parsed object or null if not found/invalid
     */
    static getObject(key) {
        try {
            const value = this.getItem(key);
            return value ? JSON.parse(value) : null;
        } catch (error) {
            ErrorHandlingService.handleError(error, `STORAGE_PARSE_${key}`);
            return null;
        }
    }

    /**
     * Set JSON object in localStorage
     * @param {string} key - The key to set
     * @param {object} obj - The object to stringify and store
     * @returns {boolean} Success status
     */
    static setObject(key, obj) {
        try {
            const value = JSON.stringify(obj);
            return this.setItem(key, value);
        } catch (error) {
            ErrorHandlingService.handleError(error, `STORAGE_STRINGIFY_${key}`);
            return false;
        }
    }

    /**
     * Generic setters/getters used by newer code paths
     * - If value is an object, it is stored as JSON
     * - Primitive values are stored as-is
     * - Getter will attempt JSON.parse and fall back to raw string on failure
     */
    static set(key, value) {
        if (typeof value === 'object') {
            return this.setObject(key, value);
        }
        return this.setItem(key, value);
    }

    static get(key) {
        const raw = this.getItem(key);
        if (raw === null) return null;

        try {
            return JSON.parse(raw);
        } catch (error) {
            // Not JSON, return raw string
            return raw;
        }
    }

    // Auth-specific methods
    static getAuthToken() {
        return authStorage.getToken();
    }

    static setAuthToken(token) {
        const result = authStorage.setToken(token);
        this.notifyAuthChanged();
        return result;
    }

    static removeAuthToken() {
        const result = authStorage.clearToken();
        this.notifyAuthChanged();
        return result;
    }

    static getLastRoute() {
        return this.getItem(this.KEYS.LAST_ROUTE);
    }

    static setLastRoute(route) {
        return this.setItem(this.KEYS.LAST_ROUTE, route);
    }

    static removeLastRoute() {
        return this.removeItem(this.KEYS.LAST_ROUTE);
    }

    /**
     * Get auth token with fallback to multiple possible keys
     * (for backward compatibility with existing code)
     */
    static getAuthTokenWithFallback() {
        return this.getAuthToken();
    }

    /**
     * Clear all auth-related data
     */
    static clearAuthData() {
        authStorage.clearSession();
        this.removeLastRoute();
        this.removeItem('userData');
        this.removeItem('tokenExpiry');
        this.notifyAuthChanged();
        return true;
    }

    /**
     * Check if localStorage is available
     * @returns {boolean} Availability status
     */
    static isAvailable() {
        try {
            const test = '__storage_test__';
            localStorage.setItem(test, test);
            localStorage.removeItem(test);
            return true;
        } catch (error) {
            return false;
        }
    }
}

export default StorageService;
